// 9-3-15 7:43
/*
 * options: {
   drill: bool
   skills: [string, or int]
   maxQuestions: int // for drill mode
   questions: ["F# min7[/bass]", etc...]
   spacing: "open" "close", default "close"
    // when "open" grandStaff is provided, when "close" one staff
   format: {
     inversion: "full", "abbreviation"*DEFAULT, "figures", false
     quality: "full", "abbreviation"*DEFAULT, "symbol"
   },
   partialCredit: bool, when true 1/2 credit if only inversion is wrong
 }

 */

import { clefNames } from 'shared/definitions/musicTheory/clefs.ts';
import InteractivePrototype from './interactivePrototype.ts';
import utils from '../utils.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import * as Notation from '../notation.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';

const seventhSkills = [
  'major_seventh_chords',
  'minor_seventh_chords',
  'dominant_seventh_chords',
  'diminished_seventh_chords',
  'half_diminished_seventh_chords',
];
const triadSkills = ['major_triads', 'minor_triads', 'augmented_triads', 'diminished_triads'];

const validSkillsCheck = (options, skills) => {
  // Do we have a valid skills set?
  const triadSpacingOrInversion =
    skills.indexOf('triad_figured_bass') !== -1 ||
    skills.indexOf('triad_inversion') !== -1 ||
    skills.indexOf('triad_spacing') !== -1;
  const noTriadIntersection = !utils.intersects(triadSkills, skills);

  if (triadSpacingOrInversion && noTriadIntersection) {
    options.skills = options.skills.concat(triadSkills);
  }

  const seventhSpacingOrInversion =
    skills.indexOf('seventh_chord_figured_bass') !== -1 ||
    skills.indexOf('seventh_chord_inversion') !== -1 ||
    skills.indexOf('seventh_chord_spacing') !== -1;
  const noSeventhIntersection = !utils.intersects(seventhSkills, skills);

  if (seventhSpacingOrInversion && noSeventhIntersection) {
    options.skills = options.skills.concat(seventhSkills);
  }
};

class ChordDraw extends InteractivePrototype {
  // Public constructor
  constructor(displayDiv, options = {}) {
    const defaultOptions = {
      format: {
        inversion: 'abbreviation',
        quality: 'abbreviation',
      },
      spacing: 'close',
    };

    const skills = (options.skills || []).map(Skills.makeString);

    validSkillsCheck(options, skills);

    defaultOptions.spacing = utils.intersects(['triad_spacing', 'seventh_chord_spacing'], skills)
      ? 'open'
      : 'close';

    const figuresIntersection = utils.intersects(
      ['triad_figured_bass', 'seventh_chord_figured_bass'],
      skills
    );
    const numbersIntersection = utils.intersects(
      ['triad_inversion', 'seventh_chord_inversion'],
      skills
    );

    if (numbersIntersection && figuresIntersection) {
      defaultOptions.format.inversion = utils.shuffle(['figures', 'numbers'])[0];
    } else {
      defaultOptions.format.inversion = figuresIntersection
        ? 'figures'
        : numbersIntersection
        ? 'numbers'
        : false;
    }

    options = utils.merge(defaultOptions, options, true);
    super();
    this.targetSpeedFromSkills = [
      'major_triads',
      'minor_triads',
      'augmented_triads',
      'diminished_triads',
      'triad_spacing',
      'triad_inversion',
      'triad_figured_bass',
      'major_seventh_chords',
      'minor_seventh_chords',
      'dominant_seventh_chords',
      'diminished_seventh_chords',
      'half_diminished_seventh_chords',
      'seventh_chord_spacing',
      'seventh_chord_inversion',
      'seventh_chord_figured_bass',
    ];

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      editor: {
        fixedWidth: 130,
        ariaTitle: {
          keySignature: false,
          rhythm: false,
          barline: false,
          clef: true,
        },
      },
      notation: false,
      letters: {
        checkButton: options.testMode ? 'Submit' : true,
        accidentalButtons: { noDoubleTap: true },
        trashButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.skillsCalculator = new SkillCalculator();
    super.init(displayDiv, options);
  }

  initializeMusic() {
    function makeStaff(clefName) {
      const clef = new Notation.Clef(clefName);
      const staff = new Notation.Staff(clef);
      const nh = new Notation.NoteHolder('', 'w');
      nh.ghostIfEmpty = true;
      const options = {
        pitch: true,
        accidental: true,
        deleteable: ['ghost'],
        selectable: true,
        maxNotes: 8,
      };
      nh.editable = new Notation.Editable(nh, options);
      const measure = new Notation.Measure(nh);
      const voice = new Notation.Voice(measure);
      voice.setStaff(staff);
      return {
        staff,
        voice,
        noteHolder: nh,
        clef,
      };
    }

    const keySig = new Notation.KeySignature('C', 'Major');
    this.staves =
      this.options.spacing === 'open'
        ? ['treble', 'bass'].map(makeStaff, this)
        : ['treble'].map(makeStaff, this);

    if (this.staves.length === 2) {
      this.staves[0].noteHolder.editable.linkedEditable = this.staves[1].noteHolder.editable;
      this.staves[1].noteHolder.editable.linkedEditable = this.staves[0].noteHolder.editable;
    }
    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([keySig, staff.staff, staff.voice]),
      []
    );

    this.music = new Notation.Music(musicElements);
  }

  initializeEditor() {
    super.initializeEditor();
    function uncolor(note) {
      if (note.color) {
        delete note.color;
        /* jshint validthis:true */
        this.editor.redraw();
      }
    }
    this.editor.addListener('notePitchChange', uncolor, this);
    this.editor.addListener('noteAccidentalChange', uncolor, this);
  }

  generateQuestions(questions) {
    this.qg = new QuestionGenerator();

    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : // Both checkpoint & regular:
        this.processManualQuestions(questions);

    this.maxQuestions = this.questions.length;
    this.questionPrompt = 'Draw {question}';
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>{question} of ${this.maxQuestions}</small>`;
    }
  }

  processManualQuestions(questions) {
    questions = questions || this.options.questions;
    return questions.map(this.processQuestion, this);
  }

  generateDrillQuestions() {
    return this.qg
      .getChords(this.options.skills, this.options.maxQuestions || 10)
      .map(this.processQuestion, this);
  }

  processQuestion(questionText, index) {
    // First we find any clef specified in the question, get it, and remove
    // it from the question text. Having a slash in the question text breaks
    // qualities because of half-dim 7th chords (o/7).
    const clef = clefNames.find((potentialClef) => questionText.includes(potentialClef));
    if (clef) questionText = questionText.replace(`/${clef}`, '');

    console.log({ questionText });
    // Split on any slash, except one before a 7, just to be sure slashes are gone.
    const questionParts = questionText.split(/\/[^7]/);
    const chord = new Notation.Chord(questionParts[0]);

    const skills = [
      ...this.qg.getChordSkills(chord),
      ...this.qg.getChordInversionSkills(chord, this.options.format.inversion === 'figures'),
    ];

    const displayOptions = {
      inversion: this.options.format.inversion === 'numbers' ? 'symbol' : 'figures',
      quality: this.options.format.quality,
      accidentalFont: true,
    };
    const chordString = chord.toHTML(displayOptions);

    return {
      chord,
      clef: clef || (Math.random() > 0.5 ? 'treble' : 'bass'),
      skills,
      text: [chordString, index + 1],
    };
  }

  newQuestion() {
    super.newQuestion();

    this.wrongAnswers = 0;
    this.editor.deselectAll();
    this.staves.forEach((staff) => {
      staff.noteHolder.length = 0;
    });
    if (this.options.spacing !== 'open') {
      this.staves[0].staff.clef.setName(this.currentQuestion.clef);
    }

    this.editor.renderer.render();
  }

  removeNotesFromNoteHolder(count) {
    const noteHolder = this.chord.noteHolder;

    for (let i = 0; i < count; i++) {
      noteHolder.remove(noteHolder.notes[0]);
    }

    return noteHolder;
  }

  getQuestionPrompt() {
    let questionPrompt;
    const testMode = this.optionsTrue('testMode');
    const currentQuestion = this.questions[this.questionNumber].question;

    if (testMode === true) {
      questionPrompt = `Draw the chord ${currentQuestion.root}${currentQuestion.quality}`;
    } else {
      questionPrompt = `Draw the chord ${currentQuestion.root}${currentQuestion.quality}<br/> Question #{question} of ${this.questions.length}`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      return Promise.resolve(this.newQuestion());
    });
  }

  getQuestionParts(question) {
    const questionParts = question.split('/');

    if (questionParts[1] === 'o' && questionParts[2] === '7') {
      questionParts[1] = `${questionParts[1]}/${questionParts[2]}`;
      questionParts[2] = questionParts[3];
    }

    return questionParts;
  }

  addListeners() {
    this.letterPanel.addCheckCallback(() => this.checkAnswer());
    this.letterPanel.addAccidentalCallback(this.editor.setAccidental, this.editor);
    this.letterPanel.addCustomButtonCallback(this.editor.delete, this.editor);
  }

  checkAnswer(forceSubmit) {
    if (this.rightAnswerDelay && !forceSubmit) return undefined;
    this.answerAttempts++;

    const noteHolder = new Notation.NoteHolder(
      this.staves.reduce((arr, staff) => arr.concat(staff.noteHolder.notes), [])
    );

    if (!forceSubmit && noteHolder.length < 3) {
      return this.checkMark.warn('You must draw at least 3 notes to make a chord.');
    }

    const correctChord = this.currentQuestion.chord.equalsIgnoreInversion(noteHolder);
    const correctInversion = this.currentQuestion.chord.equalsIgnoreOctave(noteHolder);

    const correct = !this.options.format.inversion
      ? 1 * correctChord
      : correctInversion
      ? 1
      : correctChord
      ? 0.5
      : 0;

    const answerAndDataObj = {
      correct,
      noteHolder,
      correctInversion: !!(!this.options.format.inversion || this.correctInversion),
      correctChord,
    };

    if (forceSubmit && this.options.testMode) {
      return this.getAnswerAndData(answerAndDataObj);
    }
    if (this.options.testMode) {
      return this.checkpointAnswer(answerAndDataObj);
    }

    if (correct === 1) {
      // Did they draw an open spacing chord? If so -- give them credit for it!
      if (noteHolder.isOpenSpacing()) {
        if (this.currentQuestion.chord.isSeventh) {
          this.currentQuestion.skills.push('seventh_chord_spacing');
        } else {
          this.currentQuestion.skills.push('triad_spacing');
        }
      }

      this.rightAnswer(1100);
    } else if (this.wrongAnswers++) {
      this.wrongAnswer(this.getFeedbackString(answerAndDataObj));
      if (this.wrongAnswers > 2) {
        this.editorFeedback(answerAndDataObj);
      }
    } else {
      this.wrongAnswer();
    }
    return undefined;
  }

  /* @param answerObj {
      correct: 0, 0.5, 1,
      noteHolder: student answer noteHolder
    }
  */
  getFeedbackString(answerObj) {
    const noteHolder = answerObj.noteHolder;
    const chord = this.currentQuestion.chord;
    const studentChord = Notation.Chord.fromNoteHolder(noteHolder);

    if (!studentChord) {
      return 'These notes do not form any chord.';
    }

    const rootMatch = chord.root.equalsIgnoreOctave(studentChord.root);
    const seventhMatch = chord.isSeventh === studentChord.isSeventh;
    const qualityMatch = chord.quality === studentChord.quality;
    const inversionMatch = answerObj.correctInversion;

    if (!rootMatch) {
      return `You've written ${studentChord.root.aOrAn()} ${studentChord.root.toShortString(
        true
      )} chord instead of ${chord.root.aOrAn()} ${chord.root.toShortString(true)} chord.`;
    }
    if (!seventhMatch) {
      return (
        `You've written a ${studentChord.isSeventh ? '7th chord' : 'triad'} instead ` +
        `of a ${chord.isSeventh ? '7th chord' : 'triad'}.`
      );
    }
    if (!qualityMatch) {
      const qualityString = studentChord.getQualityString(1, true, true);
      const correctQualityString = chord.getQualityString(1, true, true);
      return `You've written ${utils.aOrAn(
        qualityString
      )} ${qualityString} chord, instead of ${utils.aOrAn(
        correctQualityString
      )} ${correctQualityString} chord.`;
    }
    if (!inversionMatch) {
      return `You've written a ${studentChord.getInversionString(
        'full'
      )} chord instead of a ${chord.getInversionString('full')} one.`;
    }

    return '';
  }

  editorFeedback(answerObj) {
    const chord = this.currentQuestion.chord;
    this.editor.deselectAll();
    answerObj.noteHolder.forEach((note) => {
      if (!chord.noteHolder.noteExistsAtIgnoreOctave(note)) {
        note.color = 'red';
      }
    });
    this.editor.redraw();
  }

  playAnswer() {
    this.player.playMusic(this.music, { tempo: 180 });
  }

  getAnswerAndData(obj) {
    if (!obj) {
      return this.checkAnswer(true);
    }
    const noteHolder = obj.noteHolder;
    const correct = obj.correct;
    const chord = this.currentQuestion.chord;
    const questionText = `Draw ${this.currentQuestion.text[0]}`;

    this.editorFeedback(obj);

    return {
      question: chord.toString({ inversion: this.options.format.inversion }),
      questionType: 'ChordDraw',
      questionText,
      questionSVG: this.editor.renderer.exportSVG(),
      studentAnswerText: noteHolder.toString(),
      correctAnswerText: chord.noteHolder.toString(),
      rightAnswers: correct,
      wrongAnswers: 1 - correct,
      skills: this.currentQuestion.skills,
    };
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }
}

ChordDraw.registry = [
  {
    description: 'Write triads (root position, close spacing)',
    skills: ['major_triads', 'minor_triads', 'augmented_triads', 'diminished_triads'],
  },
  {
    description: 'Write seventh chords (root position, close spacing)',
    skills: [
      'major_seventh_chords',
      'minor_seventh_chords',
      'dominant_seventh_chords',
      'half_diminished_seventh_chords',
      'diminished_seventh_chords',
    ],
  },
  {
    description: 'Draw chords on the staff',
    skills: [
      'major_triads',
      'minor_triads',
      'augmented_triads',
      'diminished_triads',
      'triad_figured_bass',
      'triad_inversion',
      'major_seventh_chords',
      'minor_seventh_chords',
      'dominant_seventh_chords',
      'half_diminished_seventh_chords',
      'diminished_seventh_chords',
      'seventh_chord_inversion',
      'seventh_chord_figured_bass',
    ],
    alsoTrains: ['seventh_chord_spacing', 'triad_spacing'],
  },
];
export default ChordDraw;
